import axios, { AxiosPromise, AxiosRequestConfig } from "axios";
import { AES, enc } from "crypto-js";
import Cookies from "js-cookie";
import Pagination from "@/core/models/Pagination";
import CONSTANTS from "@/core/utils/constants";
import CONFIGS from "@/core/utils/configs";
import type { CompanyWithDomain } from "@/hooks/useDomainSelector.types";
import {
  teamsDarkTheme,
  teamsHighContrastTheme,
  teamsLightTheme,
  Theme,
} from "@fluentui/react-components";

export const sendAsyncRequest = (
  method: "post" | "get" | "put" | "patch" | "delete",
  url: string,
  authorization?: string,
  params?: { [key: string]: string | number },
  data?: { [key: string]: unknown },
  contentType?: string,
  baseUrl?: string,
): AxiosPromise<unknown> => {
  const { useStore } = require("@/store/store");
  const baseURL = baseUrl ?? useStore.getState().config.api_end_point;
  let config: AxiosRequestConfig = {
    baseURL,
    timeout: 20000,
    url,
    method,
    headers: {
      Accept: "application/json",
      "content-type": contentType ? contentType : "application/json",
    },
  };
  if (params) config.params = params;
  if (data) config.data = data;
  if (authorization) config.headers = { ...config.headers, authorization };
  return axios(config);
};

export const formatRange = (range: string): Pagination => {
  try {
    const total = range.split("/")[1];
    const current = range.split("/")[0].split("-")[0];
    return {
      lastPage: Math.ceil(parseInt(total) / CONSTANTS.PER_PAGE),
      currentPage: parseInt(current) / CONSTANTS.PER_PAGE + 1,
      total: parseInt(total),
    };
  } catch {
    return {
      lastPage: 1,
      currentPage: 1,
      total: 0,
    };
  }
};

export const setLocalStorage = (key: string, data: string) => {
  if (CONFIGS.PRIVATE_KEY) {
    const cryptedData = AES.encrypt(data, CONFIGS.PRIVATE_KEY).toString();
    localStorage.setItem(key, cryptedData);
  }
};

export function getTeamsTheme(theme: string | undefined): [Theme, string] {
  switch (theme) {
    case "dark":
      return [teamsDarkTheme, CONSTANTS.GLOBAL_CLASSES.DARK];
    case "contrast":
      return [teamsHighContrastTheme, CONSTANTS.GLOBAL_CLASSES.CONTRAST];
    default:
      return [teamsLightTheme, CONSTANTS.GLOBAL_CLASSES.DEFAULT];
  }
}

export const getLocalStorage = (key: string) => {
  try {
    const cryptedData = localStorage.getItem(key);
    if (cryptedData === null || !CONFIGS.PRIVATE_KEY) return null;
    const data = AES.decrypt(cryptedData, CONFIGS.PRIVATE_KEY).toString(
      enc.Utf8,
    );
    return data;
  } catch {
    return null;
  }
};

export const setCookies = (key: string, expires: Date, data: string) => {
  const cryptedData = AES.encrypt(data, CONFIGS.PRIVATE_KEY).toString();

  Cookies.set(key, cryptedData, {
    expires,
    sameSite: "None",
    secure: true,
  });
};

export const getCookies = (key: string) => {
  try {
    const cryptedData = Cookies.get(key);
    if (!cryptedData) return null;
    const data = AES.decrypt(cryptedData, CONFIGS.PRIVATE_KEY).toString(
      enc.Utf8,
    );
    return data;
  } catch {
    return null;
  }
};

function slugifyBranch(branch: string): string {
  return branch
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export const getCompanyKey = ({
  apiDomain,
  id,
}: Pick<CompanyWithDomain, "apiDomain" | "id">): string => `${apiDomain}|${id}`;

export function getSubdomain(
  name: string,
  custom: number,
  apiDomain: string,
): string {
  if (CONFIGS.ENVIRONMENT === "preproduction" && CONFIGS.REVIEW_APP_BRANCH) {
    const slug = slugifyBranch(CONFIGS.REVIEW_APP_BRANCH);
    return `https://${slug}.web.reviewapps.riseup.ai/`;
  }
  if (
    CONFIGS.ENVIRONMENT === "development" ||
    CONFIGS.ENVIRONMENT === "development-ovh"
  ) {
    return `https://dev.${CONFIGS.BASE_DOMAINMAIL}/`;
  }
  if (CONFIGS.ENVIRONMENT === "integration") {
    return `https://integration.${CONFIGS.BASE_DOMAINMAIL}/`;
  }
  if (
    CONFIGS.ENVIRONMENT === "preproduction" ||
    CONFIGS.ENVIRONMENT === "preproduction-ovh"
  ) {
    return `https://preprod.${CONFIGS.BASE_DOMAINMAIL}/`;
  }
  if (CONFIGS.ENVIRONMENT === "@env@") {
    return "https://localhost/";
  }
  let subdomainStr = `https://${name}`;
  if (custom && custom > 0) {
    subdomainStr += "/";
  } else {
    subdomainStr += `.${CONFIGS.BASE_DOMAINS[apiDomain] ?? CONFIGS.BASE_DOMAINMAIL}/`;
  }
  return subdomainStr;
}
